import React, { memo } from 'react'
import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Chart from 'react-apexcharts'

import DashboardCard from '../shared/DashboardCard/DashboardCard'
import ChartLabels from '../shared/ChartLabels/ChartLabels'
import { buildChartOptions } from '../../util/chart/chart.helpers'
import { DATE_FORMATS, ISO_WEEK, SHORT_DATE_FORMAT } from '../../constants/date'
import { isMobile } from '../../util/media.helpers'
import { formatTicks } from '../../util/string.helpers'
import { ANALYTICS_INTERVAL_FILTERS } from '../../constants/analytics'
import { buildTransactionsChartColors } from '../../util/chart/transactionsGraph.helpers'
import { formatTimeToMoment } from '../../util/date.helpers'
import Loading from '../shared/Loading/Loading'
import { CSS_FLEX_ALIGNED } from '../../styles/common'
import { CHART_TRANSACTIONS_HEIGHT } from '../../constants/chart'

const TransactionsChart = ({
  title,
  helpText,
  filteredData,
  isProcessing,
  colorRefMethod,
  chartLabels,
  colorConfig
}) => {
  const theme = useTheme()
  const chartOptions = buildChartOptions(theme, {
    grid: {
      show: true,
      borderColor: 'rgba(0, 0, 0, .2)',
      color: '#777e89',
      strokeDashArray: 2,
      xaxis: {
        lines: {
          show: false
        }
      },
      padding: {
        left: 24,
        right: 4
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    xaxis: {
      type: 'datetime',
      labels: {
        ...(isMobile() && {
          style: {
            fontSize: '9px'
          }
        })
      }
    },
    // markers: {
    //   size: 4,
    //   border: 1
    // },
    tooltip: {
      y: {
        formatter: (value) => {
          return formatTicks(value)
        }
      },
      x: {
        formatter: (time, { seriesIndex, dataPointIndex, w }) => {
          const data =
            w?.globals?.initialSeries[seriesIndex]?.data[dataPointIndex]
          if (data) {
            const label = formatTimeToMoment(time)
            switch (data?.interval) {
              case ANALYTICS_INTERVAL_FILTERS.HOURLY:
                return `${label.format(DATE_FORMATS.HOURLY)} / ${label.format(
                  SHORT_DATE_FORMAT
                )}`

              case ANALYTICS_INTERVAL_FILTERS.DAILY:
                return label.format(DATE_FORMATS.DAILY)

              case ANALYTICS_INTERVAL_FILTERS.WEEKLY:
                return `${label
                  .startOf(ISO_WEEK)
                  .format(SHORT_DATE_FORMAT)} - ${label
                  .endOf(ISO_WEEK)
                  .format(SHORT_DATE_FORMAT)}`

              case ANALYTICS_INTERVAL_FILTERS.MONTHLY:
                return label.format(DATE_FORMATS.MONTHLY)

              case ANALYTICS_INTERVAL_FILTERS.YEARLY:
                return label.format(DATE_FORMATS.YEARLY)

              default:
                break
            }
          }
          return ''
        }
      }
    }
  })

  const dataKeys = Object.keys(filteredData)
  const chartData = dataKeys.map((dataKey) => ({
    data: filteredData[dataKey],
    name: chartLabels[dataKey]
  }))

  return (
    <DashboardCard
      title={title}
      helpText={helpText}
      contentProps={{ pr: 0, pl: 0, pt: 0 }}
    >
      <Box>
        {isProcessing ? (
          <Box sx={{ height: CHART_TRANSACTIONS_HEIGHT, ...CSS_FLEX_ALIGNED }}>
            <Loading noFullHeight />
          </Box>
        ) : (
          <Chart
            options={{
              ...chartOptions,
              colors: buildTransactionsChartColors(theme, dataKeys, colorConfig)
            }}
            series={chartData}
            type='line'
            height={CHART_TRANSACTIONS_HEIGHT}
          />
        )}
      </Box>

      <ChartLabels
        data={chartLabels}
        display='flex'
        justifyContent='center'
        size={3}
        colorRefMethod={colorRefMethod}
      />
    </DashboardCard>
  )
}

export default memo(TransactionsChart)
